import React, { useState } from "react";
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from "react-icons/fa";
import { FaTiktok, FaWhatsapp } from "react-icons/fa6";
import { useTranslation } from '../hooks/useTranslation';
import { getApiBaseUrl } from '../utils/env.js';
// Use <img src="/asset/logos/logo.webp" /> in JSX instead of import

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      const API_URL = getApiBaseUrl();
      const response = await fetch(`${API_URL}/api/subscriber`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Successfully subscribed to our newsletter!');
        setEmail('');
      } else {
        setError(data.message || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <footer className="bg-green-800 text-white py-14 px-6 flex flex-col min-h-screen mt-[100px]">
      {/* Newsletter Section */}
      <div className="max-w-8xl w-[96%] mx-auto">
        <div className="flex flex-row justify-between max-md:flex-col items-center max-md:items-start">
          <div className="flex flex-col font-body pr-3">
            <h4 className="text-lg font-semibold">{t('footer.newsletter.title')}</h4>
            <p>
              {t('footer.newsletter.description')}
            </p>
          </div>
          <form onSubmit={handleSubscribe} className="relative max-sm:w-full max-md:w-full">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('footer.newsletter.placeholder')}
              style={{ background: "#D9D9D98C" }}
              className="p-4 pr-20 rounded-full font-body placeholder-gray-700 max-md:mt-4 w-full"
              required
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 max-sm:top-[44px] max-md:top-[44px] bg-yellow-400 text px-4 py-3 rounded-full font-semibold disabled:opacity-50"
            >
              {isSubmitting ? 'Subscribing...' : t('footer.newsletter.subscribe')}
            </button>
          </form>
          
          {/* Messages */}
          {message && (
            <div className="mt-4 p-3 bg-green-100 text-green-800 rounded-lg text-sm">
              {message}
            </div>
          )}
          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-800 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-4 gap-8 mt-[50px] w-[96%] mx-auto font-body max-sm:grid-cols-1 max-w-8xl">
        {/* Formations Column */}
        <div>
          <h5 className="font-semibold pb-5 text-[20px]">{t('footer.sections.programs')}</h5>
          <ul className="space-y-3 text-[14px]">
            <li>
              <a href="/auxiliaireDeVie" className="hover:underline">
                {t('services.careAssistant.title')}
              </a>
            </li>
            <li>
              <a href="/secretariatMedical" className="hover:underline">
                {t('services.medicalSecretary.title')}
              </a>
            </li>
            <li>
              <a href="/assistantEnCabinetMedical" className="hover:underline">
                {t('services.medicalPracticeAssistant.title')}
              </a>
            </li>
            <li>
              <a href="/delegueMedical" className="hover:underline">
                {t('services.medicalDelegate.title')}
              </a>
            </li>
            <li>
              <a href="/vendeurPharmacie" className="hover:underline">
                {t('services.pharmacySalesperson.title')}
              </a>
            </li>
            <li>
              <a href="/agentTechniqueLaboratoire" className="hover:underline">
                {t('services.laboratoryTechnician.title')}
              </a>
            </li>
          </ul>
        </div>

        {/* Institution Column */}
        <div>
          <h5 className="font-semibold pb-5 text-[20px]">{t('footer.sections.institution')}</h5>
          <ul className="space-y-3 text-[14px]">
            <li>
              <a href="/" className="hover:underline">
                {t('footer.links.home')}
              </a>
            </li>
            <li>
              <a href="/about" className="hover:underline">
                {t('footer.links.about')}
              </a>
            </li>
            <li>
              <a href="/faq" className="hover:underline">
                {t('footer.links.faq')}
              </a>
            </li>
            <li>
              <a href="/formations" className="hover:underline">
                {t('footer.links.programs')}
              </a>
            </li>
            <li>
              <a href="/contact" className="hover:underline">
                {t('footer.links.contact')}
              </a>
            </li>
            <li>
              <a
                href="https://www.google.com/maps/place/ITC+Sant%C3%A9/@3.846017,11.572237,17z/data=!4m6!3m5!1s0x0:0x0!7e2!8m2!3d3.846017!4d11.572237"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {t('footer.links.location')}
              </a>
            </li>
          </ul>
        </div>

        {/* Social Media Column */}
        <div>
          <h5 className="font-semibold pb-5 text-[20px]">{t('footer.sections.socialMedia')}</h5>
          <ul className="space-y-3 text-[14px]">
            <li>
              <a
                href="https://www.linkedin.com/company/itc-sant%C3%A9/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-2"
              >
                <FaLinkedin className="text-xl" />
                LinkedIn
              </a>
            </li>
            <li>
              <a
                href="https://wa.me/651245653"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-2"
              >
                <FaWhatsapp className="text-xl" />
                Whatsapp
              </a>
            </li>
            <li>
              <a
                href="https://www.facebook.com/share/hNHiPvAJWZTjsvxP/?mibextid=LQQJ4d"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-2"
              >
                <FaFacebook className="text-xl" />
                Facebook
              </a>
            </li>

            <li>
              <a
                href="https://www.instagram.com/itc_sante_cameroun?igsh=MTF1aGVyZmNyNTN3YQ=="
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-2"
              >
                <FaInstagram className="text-xl" />
                Instagram
              </a>
            </li>
            <li>
              <a
                href="https://www.tiktok.com/@itcsant3?_t=ZS-8vbgt98kamd&_r=1"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-2"
              >
                <FaTiktok className="text-xl" />
                TikTok
              </a>
            </li>
            <li>
              <a
                href="https://youtube.com/@laminutesante-h5t?si=iGkT-Dv0wnkuIu2a"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-2"
              >
                <FaYoutube className="text-xl" />
                YouTube - La Minute Santé
              </a>
            </li>
          </ul>
        </div>

        {/* Logo Column */}
        <div className="flex items-center justify-center">
          <img src="/asset/logos/logo.webp" alt="ITC Santé Logo" className="h-24 w-auto" />
        </div>
      </div>

      {/* Copyright Section */}
      <div className="flex justify-center items-center w-full mt-auto py-6 ">
        <p className="text-sm mb-6 text-center max-w-2xl mt-12">
          {t('footer.copyright')}{" "}
          <b>Carrefour CEF, Nkoabang, Yaoundé</b>.
          <p className="text-sm text-center mt-4">
            &copy; {new Date().getFullYear()} ITC Santé. {t('footer.allRightsReserved')}.
          </p>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
